import { useIsFocused } from "expo-router/react-navigation";
import { router } from 'expo-router';
import { useLayoutEffect, useState } from 'react';

import { useNpoOnboarding } from '@/api/hooks/meHooks';
import { useUpdateNonProfitOrg } from '@/api/hooks/nonProfitOrgHooks';
import { NonProfitOrg } from '@/api/models/nonProfitOrgModels';
import NpoPLForm from '@/components/forms/NpoPLForm';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { PLData } from '@/lib/validations/NpoOnboardingSchema';
import { containsName, containsSection } from '@/utils/containsSection';
import { safeJsonParseArray } from '@/utils/firebaseImage';
import { safeJsonParse } from '@/utils/jsonParse';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';

const PL = () => {
  const { state: authState } = useAuth();
  const { state: npoScreensState, setCurrentPage } = useNpoLoginScreenState();
  const { mutateAsync: onBoardingUpdate, isPending: onBoardingIsPending } = useNpoOnboarding(
    authState.signUpData?.loginEmail?.type ?? 'CHURCH'
  );
  const { mutateAsync: nonProfitUpdate, isPending: nonProfitIsPending } = useUpdateNonProfitOrg();

  const [isLoading, setIsLoading] = useState(false);
  const [fileProgress, setFileProgress] = useState<number | undefined>(undefined);
  const [indexUploading, setIndexUploading] = useState<number | undefined>(undefined);

  const isFocused = useIsFocused();
  useLayoutEffect(() => {
    if (isFocused) setCurrentPage(7);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  const nonProfitOrg = safeJsonParse<NonProfitOrg>(npoScreensState.initialNpoData ?? '');
  const initialData = {
    pL: typeof nonProfitOrg?.plStatement === 'string' ? safeJsonParseArray(nonProfitOrg.plStatement)[0] : '',
    balanceSheet: typeof nonProfitOrg?.balanceSheet === 'string' ? safeJsonParseArray(nonProfitOrg.balanceSheet)[0] : ''
  };

  const npoId = authState.signUpData?.loginEmail?.npoId;

  const onSubmit = async (data: PLData) => {
    try {
      setIsLoading(true);

      let uploadedFiles: {
        pl: string[] | [];
        balanceSheet: string[] | [];
      } = { pl: [], balanceSheet: [] };

      if (data.pL && !containsSection(data.pL, 'pl')) {
        await uploadToFireStorage(
          `non-profits-orgs/${npoId}/pl/`,
          {
            uri: data.pL,
            name: `profit_loss.${new Date().toISOString()}`
          },
          (progress) => {
            setIndexUploading(0);
            setFileProgress(progress);
          }
        ).then((storagedFiles) => {
          uploadedFiles.pl = containsName(storagedFiles.metadata.name, 'profit_loss')
            ? [storagedFiles.metadata.fullPath]
            : [];
        });
      }

      if (data.balanceSheet && !containsSection(data.balanceSheet, 'bs')) {
        await uploadToFireStorage(
          `non-profits-orgs/${npoId}/bs/`,
          {
            uri: data.balanceSheet,
            name: `balance_sheet.${new Date().toISOString()}`
          },
          (progress) => {
            setIndexUploading(1);
            setFileProgress(progress);
          }
        ).then((storagedFiles) => {
          uploadedFiles.balanceSheet = containsName(storagedFiles.metadata.name, 'balance_sheet')
            ? [storagedFiles.metadata.fullPath]
            : storagedFiles && containsName(storagedFiles.metadata.name, 'balance_sheet')
              ? [storagedFiles.metadata.fullPath]
              : [];
        });
      }

      nonProfitUpdate(
        {
          npoId: npoId ?? '',
          plStatement: data.pL ? (uploadedFiles.pl.length === 0 ? null : uploadedFiles.pl) : [],
          balanceSheet: data.balanceSheet
            ? uploadedFiles.balanceSheet.length === 0
              ? null
              : uploadedFiles.balanceSheet
            : []
        },
        {
          onSuccess: (_) => {
            onBoardingUpdate(
              {
                preferenceId: authState.preferenceId ?? '',
                pL: true
              },
              {
                onSuccess: () => {
                  setIsLoading(false);
                  setFileProgress(undefined);
                  router.navigate('/(app)/login/nonProfit/onBoarding/partyAudit');
                }
              }
            );
          },
          onError: (e) => {
            setIsLoading(false);
            console.error('Error', e);
          }
        }
      );
    } catch (e) {
      setIsLoading(false);
      setFileProgress(undefined);
      setIndexUploading(undefined);
      console.error(e);
    }
  };

  return (
    <NpoPLForm
      initialData={initialData}
      isLoading={isLoading || onBoardingIsPending || nonProfitIsPending}
      indexUploading={indexUploading}
      fileProgress={fileProgress}
      onSubmitTapped={onSubmit}
    />
  );
};

export default PL;
